import React, { useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid2,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { EGSpeedDial } from "../../../../../../MainComponents/Button/SpeedDial/SpeedDial";
import { BoldJudge, OutlineEdit, TrashCan, MoneyTick, BoldTickCircle, BoldCloseCircle } from "../../../../../../Icon";
import { useTranslation } from "react-i18next";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import Comments from "../../../../Information/Comments";
import EGDatePicker from "../../../../../../MainComponents/DatePicker/EGDatePicker";
import { updateSanctions, createTags, deleteTags } from "../../../../../../../apis";
import { useParams } from "react-router";
import { useSnackbar } from "notistack";
import { DrawerContainer } from "../../../../Investigation2/components/DrawerContainer";
import { InvestigationButton } from "../../../../Investigation2/components/InvestigationButton";

export const SanctionsAndCompensations = ({
  actions,
  actionsType,
  deleteFunction,
  openModalConfirm,
  closeModalConfirm,
  hdlOpenDialog,
  reloadData,
  reportRender,
}) => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  const { id } = useParams();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { userInfo, email, username } = useSelector((state) => state.auth);
  const { involvedType, sanctionsOptions, benefitOptions } = useReportContext();

  // Estado para modal de aprobación
  const [approvalDialog, setApprovalDialog] = useState({
    open: false,
    action: null,
    mode: "approve", // "approve" o "revoke"
  });
  const [approvalDate, setApprovalDate] = useState(dayjs());
  const [selectedSanctions, setSelectedSanctions] = useState([]);
  const [approvalMessage, setApprovalMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState(null); // "all" | "none" | "custom"

  // Abrir dialog de aprobación
  const openApprovalDialog = (action) => {
    // Obtener todas las sanciones del action
    const allSanctions = [
      ...(action.sanctions || []),
      ...(action.another || []).map((a) => a.another_sanction || a.id),
    ];
    setSelectedSanctions(allSanctions);
    setApprovalDate(dayjs());
    setSelectionMode(null);
    setApprovalDialog({ open: true, action, mode: "approve" });
  };

  // Abrir dialog para revocar
  const openRevokeDialog = (action) => {
    setApprovalDialog({ open: true, action, mode: "revoke" });
  };

  // Cerrar dialog
  const closeApprovalDialog = () => {
    setApprovalDialog({ open: false, action: null, mode: "approve" });
    setSelectedSanctions([]);
    setApprovalDate(dayjs());
    setApprovalMessage("");
    setSelectionMode(null);
  };

  // Toggle selección de sanción individual
  const toggleSanction = (sanctionValue) => {
    setSelectedSanctions((prev) =>
      prev.includes(sanctionValue)
        ? prev.filter((s) => s !== sanctionValue)
        : [...prev, sanctionValue]
    );
  };

  // Aceptar todas las sanciones
  const handleAcceptAll = (action) => {
    const allSanctions = [
      ...(action.sanctions || []),
      ...(action.another || []).map((a) => a.another_sanction || a.id),
    ];
    setSelectedSanctions(allSanctions);
    setSelectionMode("all");
  };

  // Rechazar todas las sanciones
  const handleRejectAll = () => {
    setSelectedSanctions([]);
    setSelectionMode("none");
  };

  // Habilitar selección manual
  const handleCustomSelection = () => {
    setSelectionMode("custom");
  };

  // Enviar aprobación
  const handleApprove = async () => {
    const { action } = approvalDialog;
    if (!action) return;

    setIsLoading(true);
    const processing = enqueueSnackbar(`${t("Resolution.approve")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      const dataToUpdate = {
        report_identifier: id,
        id: action.id,
        sanctions_approved: true,
        approved_sanctions: selectedSanctions,
        approved_date: approvalDate.format("DD/MMM/YYYY"),
        approved_by: {
          email: email,
          name: userInfo?.name || username || email,
          username: username,
          message: approvalMessage,
        },
      };

      await updateSanctions(dataToUpdate);

      // Marcar bandera según si se aprobaron o rechazaron sanciones
      const flagColor = selectedSanctions.length > 0 ? "orange" : "gray";
      await createTags({ id, key: flagColor });

      closeSnackbar(processing);
      enqueueSnackbar(t("Resolution.sanctions_approved_successfully"), {
        variant: "success",
      });
      closeApprovalDialog();
      if (reloadData) reloadData();
      if (reportRender) reportRender();
    } catch (error) {
      console.error(error);
      closeSnackbar(processing);
      enqueueSnackbar("Error", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Revocar aprobación
  const handleRevoke = async () => {
    const { action } = approvalDialog;
    if (!action) return;

    setIsLoading(true);
    const processing = enqueueSnackbar(`${t("Resolution.revoke_approval")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      const dataToUpdate = {
        report_identifier: id,
        id: action.id,
        sanctions_approved: false,
        approved_sanctions: [],
        approved_date: null,
        approved_by: null,
      };

      await updateSanctions(dataToUpdate);

      // Quitar banderas gray y orange al revocar (silent para evitar error 404)
      await deleteTags({ id, key: "gray", silent: true }).catch(() => {});
      await deleteTags({ id, key: "orange", silent: true }).catch(() => {});

      closeSnackbar(processing);
      enqueueSnackbar(t("Resolution.approval_revoked_successfully"), {
        variant: "success",
      });
      closeApprovalDialog();
      if (reloadData) reloadData();
      if (reportRender) reportRender();
    } catch (error) {
      console.error(error);
      closeSnackbar(processing);
      enqueueSnackbar("Error", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };
  
  const actionsTaken = {
    sanctions: {
      type: "sanctions",
      color: "orange",
      colorHover: "orangeII",
      assignedActions: t("Resolution.assigned_sanctions"),
      options: sanctionsOptions,
      another: "another_sanction",
    },
    compensations: {
      type: "benefits",
      color: "vividViolet",
      colorHover: "vividVioletII",
      assignedActions: t("Resolution.benefits_granted"),
      options: benefitOptions,
      another: "another_benefit",
    },
  };

  return (
    <Box
      px={{ xs: 0.33, md: 1 }}
      maxHeight={"40vh"}
      sx={{ overflowY: "auto" }}
      className="rounded"
    >
      {actions.map((action, i) => (
        <Box
          key={action.id}
          className={`${i < actions.length - 1 ? "border-bottom" : ""}`}
          sx={{
            minHeight: "50px",
            position: "relative",
            my: "auto",
          }}
        >
          <EGSpeedDial
            sx={{
              position: "absolute",
              right: -10,
              top: 1.5,
              scale: 0.8,
              zIndex: 1,
            }}
            tooltipPlacement="top"
            actions={[
              // Botón Aprobar/Revocar (solo para sanciones)
              ...(actionsType === "sanctions"
                ? [
                    action.sanctions_approved
                      ? {
                          icon: <MoneyTick color={theme.palette.warning.main} />,
                          name: t("Resolution.revoke_approval"),
                          direction: "top",
                          onClick: () => openRevokeDialog(action),
                        }
                      : {
                          icon: <MoneyTick color={theme.palette.success.main} />,
                          name: t("Resolution.approve"),
                          direction: "top",
                          onClick: () => openApprovalDialog(action),
                        },
                  ]
                : []),
              // Botón Editar (deshabilitado si está aprobado)
              {
                icon: (
                  <OutlineEdit
                    color={
                      action.sanctions_approved && actionsType === "sanctions"
                        ? theme.palette.grey[400]
                        : theme.palette.blue.main
                    }
                  />
                ),
                name:
                  action.sanctions_approved && actionsType === "sanctions"
                    ? t("Resolution.cannot_edit_approved")
                    : t("Resolution.edit"),
                direction: "top",
                onClick: () => {
                  if (action.sanctions_approved && actionsType === "sanctions") {
                    enqueueSnackbar(t("Resolution.cannot_edit_approved"), {
                      variant: "warning",
                    });
                    return;
                  }
                  hdlOpenDialog(actionsTaken[actionsType].type, action);
                },
              },
              // Botón Eliminar
              {
                icon: <TrashCan color={theme.palette.error.main} />,
                name: t("Resolution.delete"),
                direction: "top",
                onClick: () =>
                  openModalConfirm({
                    variant: "error",
                    title: t("Resolution.attention"),
                    titleColor: theme.palette.pink.main,
                    description1: t(
                      "Care.deleting_this_measure_will_permanently_erase_all_content"
                    ),
                    question: t("Care.do_you_want_continue"),
                    leftButtonText: t("Resolution.delete"),
                    leftButtonColor: "error",
                    leftButtonVariant: "outlined",
                    onClickLeft: () => deleteFunction(action),
                    rightButtonText: t("Care.cancel"),
                    rightButtonColor: "secondary",
                    onClickRight: () => closeModalConfirm(),
                    ethiquin: "megaphone",
                  }),
              },
            ]}
          />

          <ListItem dense sx={{ padding: 0, pb: 0.33 }}>
            <ListItemAvatar>
              <Avatar sx={{ backgroundColor: "transparent" }}>
                <EGIconButton
                  iconName={
                    action.confirm
                      ? "outline_profile_tick"
                      : "outline_profile_remove"
                  }
                  color={
                    action.confirm
                      ? actionsTaken[actionsType].color
                      : "blueGray500"
                  }
                  title={
                    action.confirm
                      ? t("Resolution.confirmed")
                      : t("Resolution.unconfirmed")
                  }
                  colorHover={
                    action.confirm
                      ? actionsTaken[actionsType].colorHover
                      : "blueGray700"
                  }
                />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  maxWidth={"90%"}
                  gap={1}
                >
                  <Box display="flex" alignItems="center">
                    <Typography
                      mr={0.5}
                      color="secondary"
                      fontWeight={600}
                      variant="body1"
                      component="span"
                    >
                      {`${involvedType[action.kind]}: `}
                    </Typography>
                    <Typography
                      color={actionsTaken[actionsType].color}
                      variant="body1"
                      component="span"
                      fontWeight={700}
                      maxWidth={"90%"}
                      textOverflow={""}
                    >
                      {action.name}
                    </Typography>
                  </Box>
                  {/* Indicador de aprobación */}
                  {actionsType === "sanctions" && (() => {
                    const totalSanctions =
                      (action.sanctions?.length || 0) +
                      (action.another?.length || 0);
                    const approvedCount = action.approved_sanctions?.length || 0;

                    let chipLabel = t("Resolution.not_approved");
                    let chipColor = "default";
                    let chipVariant = "outlined";

                    if (action.sanctions_approved) {
                      if (approvedCount === 0) {
                        chipLabel = t("Resolution.rejected");
                        chipColor = "error";
                        chipVariant = "filled";
                      } else if (approvedCount === totalSanctions) {
                        chipLabel = t("Resolution.approved");
                        chipColor = "success";
                        chipVariant = "filled";
                      } else {
                        chipLabel = t("Resolution.partially_approved");
                        chipColor = "warning";
                        chipVariant = "filled";
                      }
                    }

                    return (
                      <Tooltip
                        title={
                          action.sanctions_approved && action.approved_by
                            ? `${chipLabel} - ${t("Resolution.approved_by")}: ${action.approved_by.name || action.approved_by.email} - ${action.approved_date || ""}`
                            : t("Resolution.not_approved")
                        }
                        arrow
                      >
                        <Chip
                          size="small"
                          label={chipLabel}
                          color={chipColor}
                          variant={chipVariant}
                          sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                        />
                      </Tooltip>
                    );
                  })()}
                </Box>
              }
              secondary={
                <Stack
                  spacing={2.5}
                  display="flex"
                  alignItems="center"
                  justifyContent="normal"
                  flexDirection="row"
                  flexWrap="wrap"
                  py={0.5}
                  maxWidth={"90%"}
                  component={"span"}
                >
                  <Typography variant="body2" component="span">
                    {involvedType[action.kind || action.rol]}
                  </Typography>
                  {action.additional && (
                    <Typography variant="body2" component="span">{`${t(
                      "Resolution.employee_number"
                    )}: ${action.additional}`}</Typography>
                  )}
                  {action.relationship && (
                    <Typography variant="body2" component="span">{`${t(
                      "Resolution.job_position"
                    )}: ${action.relationship}`}</Typography>
                  )}
                  {action.details && (
                    <Typography variant="body2" component="span">{`${t(
                      "Resolution.area"
                    )}: ${action.details}`}</Typography>
                  )}
                </Stack>
              }
            />
          </ListItem>

          <Grid2 container mb={0.5}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <List
                subheader={<li />}
                dense
                sx={{
                  px: 0.5,
                  maxHeight: "25vh",
                  overflow: "auto",
                  "& ul": { padding: 0 },
                }}
              >
                <li>
                  <ul>
                    <ListSubheader
                      component={"h6"}
                      sx={{
                        px: 0.5,
                        py: 0,
                        lineHeight: "15px",
                        width: "100%",
                      }}
                    >
                      {`${actionsTaken[actionsType].assignedActions}:`}
                    </ListSubheader>
                    {action[actionsTaken[actionsType].type] &&
                      action[actionsTaken[actionsType].type].map(
                        (actionValue, i) => {
                          // Intentar obtener el label desde el objeto _label guardado
                          const labelKey = `${actionsTaken[actionsType].type}_label`;
                          const savedLabel = action[labelKey]?.[actionValue];

                          // Si no existe label guardado, buscar en las opciones
                          const displayLabel = savedLabel ||
                            actionsTaken[actionsType]?.options.find(
                              (option) => option.value === actionValue
                            )?.label || actionValue;

                          // Verificar si esta sanción está aprobada
                          const isApproved = actionsType === "sanctions" &&
                            action.approved_sanctions?.includes(actionValue);

                          return (
                            <ListItem sx={{ px: 1, py: 0 }} key={`action-${i}`}>
                              <ListItemIcon sx={{ minWidth: "25px" }}>
                                <BoldJudge
                                  color={
                                    theme.palette[
                                      actionsTaken[actionsType]?.color
                                    ].main
                                  }
                                  size={20}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="body1">{displayLabel}</Typography>
                                    {actionsType === "sanctions" && action.sanctions_approved && (
                                      isApproved ? (
                                        <BoldTickCircle color={theme.palette.success.main} size={16} />
                                      ) : (
                                        <BoldCloseCircle color={theme.palette.error.main} size={16} />
                                      )
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          );
                        }
                      )}
                    {action.another &&
                      action.another.map((another) => {
                        const anotherValue = another.another_sanction || another.id;
                        const isAnotherApproved = actionsType === "sanctions" &&
                          action.approved_sanctions?.includes(anotherValue);

                        return (
                          <ListItem
                            sx={{ px: 1, py: 0 }}
                            key={`another-${another.id}`}
                          >
                            <ListItemIcon sx={{ minWidth: "25px" }}>
                              <BoldJudge
                                color={
                                  theme.palette[actionsTaken[actionsType]?.color]
                                    .main
                                }
                                size={20}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Typography variant="body1">
                                    {another[actionsTaken[actionsType].another]}
                                  </Typography>
                                  {actionsType === "sanctions" && action.sanctions_approved && (
                                    isAnotherApproved ? (
                                      <BoldTickCircle color={theme.palette.success.main} size={16} />
                                    ) : (
                                      <BoldCloseCircle color={theme.palette.error.main} size={16} />
                                    )
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                  </ul>
                </li>
              </List>
            </Grid2>

            {/* Otras recomendaciones */}
            {action.other_recommendations && action.other_recommendations.length > 0 && (
              <Grid2 size={{ xs: 12, md: 6 }}>
                <List
                  subheader={<li />}
                  dense
                  sx={{
                    px: 0.5,
                    maxHeight: "25vh",
                    overflow: "auto",
                    "& ul": { padding: 0 },
                  }}
                >
                  <li>
                    <ul>
                      <ListSubheader
                        component={"h6"}
                        sx={{
                          px: 0.5,
                          py: 0,
                          lineHeight: "15px",
                          width: "100%",
                        }}
                      >
                        {`${t("Resolution.other_recommendations")}:`}
                      </ListSubheader>
                      {action.other_recommendations.map((recValue, i) => {
                        // Intentar obtener el label desde el objeto _label guardado
                        const savedLabel = action.other_recommendations_label?.[recValue];
                        // Si no existe, mostrar el value
                        const displayLabel = savedLabel || recValue;

                        return (
                          <ListItem sx={{ px: 1, py: 0 }} key={`other-rec-${i}`}>
                            <ListItemIcon sx={{ minWidth: "25px" }}>
                              <BoldJudge
                                color={theme.palette.primary.main}
                                size={20}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={displayLabel}
                              primaryTypographyProps={{ variant: "body1" }}
                            />
                          </ListItem>
                        );
                      })}
                    </ul>
                  </li>
                </List>
              </Grid2>
            )}

            {action.comment && (
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" color="secondary">
                  {`${t("Resolution.details")}:`}
                </Typography>
                <Comments
                  text={action.comment}
                  background="white"
                  padding={1}
                  maxLength={250}
                  border="blueGray50"
                  rounded
                />
              </Grid2>
            )}
          </Grid2>
          <Stack direction="column" sx={{px: 1, py: 0.5}}>
            {action.determination_date && (
              <Typography variant="body1" component="span">
                {`${t("Resolution.determination_date")}: ${action.determination_date}`}
              </Typography>
              )}
          </Stack>
          <Stack direction="column" sx={{px: 1, py: 0.5}}>
            {action.execution_date && (
              <Typography variant="body1" component="span">
                {`${t("Resolution.execution_date")}: ${action.execution_date}`}
              </Typography>
              )}
          </Stack>
          {/* Información de aprobación */}
          {actionsType === "sanctions" && action.sanctions_approved && action.approved_by && (
            <Stack
              direction="column"
              spacing={0.5}
              sx={{
                px: 1,
                py: 0.5,
                mt: 0.5,
                backgroundColor: theme.palette.success.light + "20",
                borderRadius: 1,
              }}
            >
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Typography variant="body2" component="span" color="success.dark">
                  {`${t("Resolution.approved_by")}: ${action.approved_by.name || action.approved_by.email}`}
                </Typography>
                {action.approved_date && (
                  <Typography variant="body2" component="span" color="success.dark">
                    {`${t("Resolution.approved_date")}: ${action.approved_date}`}
                  </Typography>
                )}
              </Stack>
              {action.approved_by.message && (
                <Typography variant="body2" component="span" color="success.dark" sx={{ fontStyle: "italic" }}>
                  {`${t("Resolution.approval_message")}: "${action.approved_by.message}"`}
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      ))}

      {/* Drawer de aprobación */}
      <DrawerContainer
        open={approvalDialog.open}
        onClose={closeApprovalDialog}
        borderColor={approvalDialog.mode === "approve" ? theme.palette.success.main : theme.palette.warning.main}
        maxWidth="450px"
      >
        <Box padding="10px 20px 20px 20px">
          <Typography
            fontSize={28}
            fontWeight={600}
            color={approvalDialog.mode === "approve" ? "success.main" : "warning.main"}
            marginBottom={2}
          >
            {approvalDialog.mode === "approve"
              ? t("Resolution.approve_sanctions")
              : t("Resolution.revoke_approval")}
          </Typography>

          {approvalDialog.mode === "approve" ? (
            <Stack spacing={2}>
              {/* Info del infractor */}
              <Box
                sx={{
                  backgroundColor: `${theme.palette.orange.main}10`,
                  borderRadius: "10px",
                  padding: "12px 16px",
                  border: `1px solid ${theme.palette.orange.main}30`,
                }}
              >
                <Typography fontSize={14} fontWeight={600} color="orange.main">
                  {approvalDialog.action?.name}
                </Typography>
                <Typography fontSize={12} color="secondary">
                  {involvedType[approvalDialog.action?.kind]}
                </Typography>
              </Box>

              {/* Sanciones a aprobar */}
              <Box
                sx={{
                  backgroundColor: `${theme.palette.success.main}08`,
                  borderRadius: "10px",
                  padding: "12px 16px",
                  border: `1px solid ${theme.palette.success.main}20`,
                }}
              >
                <Typography fontSize={13} fontWeight={700} color="success.main" mb={1}>
                  {t("Resolution.sanctions_to_approve")}:
                </Typography>

                {/* Botones de selección */}
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
                  <InvestigationButton
                    label={t("Resolution.accept_all")}
                    variant="success"
                    icon="bold_tick_circle"
                    onClick={() => handleAcceptAll(approvalDialog.action)}
                    outlined={selectionMode !== "all"}
                  />
                  <InvestigationButton
                    label={t("Resolution.reject_all")}
                    variant="danger"
                    icon="bold_close_circle"
                    onClick={handleRejectAll}
                    outlined={selectionMode !== "none"}
                  />
                  <InvestigationButton
                    label={t("Resolution.choose")}
                    icon="outline_edit"
                    onClick={handleCustomSelection}
                    outlined={selectionMode !== "custom"}
                  />
                </Stack>

                {/* Lista de sanciones */}
                <Box sx={{ pl: 2, borderLeft: `3px solid ${theme.palette.success.main}` }}>
                  {approvalDialog.action?.sanctions?.map((sanctionValue, idx) => {
                    const labelKey = `sanctions_label`;
                    const savedLabel = approvalDialog.action[labelKey]?.[sanctionValue];
                    const displayLabel =
                      savedLabel ||
                      sanctionsOptions?.find((opt) => opt.value === sanctionValue)?.label ||
                      sanctionValue;

                    return (
                      <FormControlLabel
                        key={`sanction-${idx}`}
                        control={
                          <Checkbox
                            checked={selectedSanctions.includes(sanctionValue)}
                            onChange={() => toggleSanction(sanctionValue)}
                            disabled={selectionMode !== "custom"}
                            icon={<BoldCloseCircle color={theme.palette.error.main} size={22} />}
                            checkedIcon={<BoldTickCircle color={theme.palette.success.main} size={22} />}
                          />
                        }
                        label={displayLabel}
                        sx={{ display: "block" }}
                      />
                    );
                  })}
                  {approvalDialog.action?.another?.map((another, idx) => {
                    const value = another.another_sanction || another.id;
                    return (
                      <FormControlLabel
                        key={`another-${idx}`}
                        control={
                          <Checkbox
                            checked={selectedSanctions.includes(value)}
                            onChange={() => toggleSanction(value)}
                            disabled={selectionMode !== "custom"}
                            icon={<BoldCloseCircle color={theme.palette.error.main} size={22} />}
                            checkedIcon={<BoldTickCircle color={theme.palette.success.main} size={22} />}
                          />
                        }
                        label={another.another_sanction}
                        sx={{ display: "block" }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* DatePicker */}
              <EGDatePicker
                label={t("Resolution.approved_date")}
                value={approvalDate}
                onChange={(newValue) => setApprovalDate(newValue)}
                maxDate={dayjs()}
              />

              {/* Info del usuario que aprueba */}
              <Box
                sx={{
                  backgroundColor: theme.palette.grey[100],
                  borderRadius: "8px",
                  padding: "10px 14px",
                }}
              >
                <Typography fontSize={12} color="textSecondary">
                  {`${t("Resolution.approved_by")}: ${userInfo?.name || username || email}`}
                </Typography>
              </Box>

              {/* Mensaje de aprobación (obligatorio) */}
              <TextField
                label={t("Resolution.approval_message")}
                placeholder={t("Resolution.approval_message_placeholder")}
                multiline
                rows={3}
                value={approvalMessage}
                onChange={(e) => setApprovalMessage(e.target.value)}
                required
                error={approvalMessage.trim() === ""}
                helperText={approvalMessage.trim() === "" ? t("Resolution.approval_message_required") : ""}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.success.main,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: theme.palette.success.main,
                  },
                }}
              />

              {/* Botones */}
              <Stack spacing={1}>
                <InvestigationButton
                  label={t("Resolution.save")}
                  variant="success"
                  icon="mark_check_bold"
                  fullWidth
                  onClick={handleApprove}
                  disabled={isLoading || approvalMessage.trim() === ""}
                  loading={isLoading}
                />
                <InvestigationButton
                  label={t("Care.cancel")}
                  icon="close_icon"
                  fullWidth
                  outlined
                  onClick={closeApprovalDialog}
                />
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2}>
              {/* Info del infractor */}
              <Box
                sx={{
                  backgroundColor: `${theme.palette.warning.main}10`,
                  borderRadius: "10px",
                  padding: "12px 16px",
                  border: `1px solid ${theme.palette.warning.main}30`,
                }}
              >
                <Typography fontSize={14} fontWeight={600} color="warning.main">
                  {approvalDialog.action?.name}
                </Typography>
                <Typography fontSize={12} color="secondary">
                  {involvedType[approvalDialog.action?.kind]}
                </Typography>
              </Box>

              <Typography fontSize={16} color="oxfordBlue">
                {t("Resolution.confirm_revoke_approval")}
              </Typography>

              {approvalDialog.action?.approved_by && (
                <Box
                  sx={{
                    backgroundColor: theme.palette.grey[100],
                    borderRadius: "8px",
                    padding: "10px 14px",
                  }}
                >
                  <Typography fontSize={12} color="textSecondary">
                    {`${t("Resolution.approved_by")}: ${approvalDialog.action.approved_by.name || approvalDialog.action.approved_by.email}`}
                  </Typography>
                  <Typography fontSize={12} color="textSecondary">
                    {`${t("Resolution.approved_date")}: ${approvalDialog.action.approved_date}`}
                  </Typography>
                </Box>
              )}

              {/* Botones */}
              <Stack spacing={1}>
                <InvestigationButton
                  label={t("Resolution.revoke_approval")}
                  variant="danger"
                  icon="close_circle"
                  fullWidth
                  onClick={handleRevoke}
                  disabled={isLoading}
                  loading={isLoading}
                />
                <InvestigationButton
                  label={t("Care.cancel")}
                  icon="close_icon"
                  fullWidth
                  outlined
                  onClick={closeApprovalDialog}
                />
              </Stack>
            </Stack>
          )}
        </Box>
      </DrawerContainer>
    </Box>
  );
};
